import { fetchReportPreviewChunk } from "../api/reportPreviewApi";
import { CHUNK_PAGE_SIZE, PRELOAD_PAGE_SIZE } from "../model/pageTypes";

function cacheKey(propertyMasterId, inspectionReferenceNumber) {
  return `${propertyMasterId}:${inspectionReferenceNumber}`;
}

/** @type {Map<string, { data: object, pageSize: number, fetchedAt: number }>} */
const chunkStore = new Map();

/** @type {Map<string, Promise<object>>} */
const inflight = new Map();

/**
 * Background preload for Property View Details (small first payload).
 * Does not block; dedupes in-flight requests.
 */
export function prefetchReportPreviewFirstChunk(
  propertyMasterId,
  inspectionReferenceNumber,
  pageSize = PRELOAD_PAGE_SIZE,
) {
  if (!propertyMasterId || !inspectionReferenceNumber) {
    return Promise.resolve(null);
  }

  const key = cacheKey(propertyMasterId, inspectionReferenceNumber);
  const existing = chunkStore.get(key);
  if (existing?.data) {
    return Promise.resolve(existing.data);
  }

  const inflightKey = `${key}:${pageSize}`;
  if (inflight.has(inflightKey)) {
    return inflight.get(inflightKey);
  }

  const promise = fetchReportPreviewChunk({
    propertyMasterId,
    inspectionReferenceNumber,
    pageNumber: 1,
    pageSize,
  })
    .then((data) => {
      chunkStore.set(key, {
        data,
        pageSize,
        fetchedAt: Date.now(),
      });
      return data;
    })
    .catch((err) => {
      console.warn("Report preview preload failed:", err);
      return null;
    })
    .finally(() => {
      inflight.delete(inflightKey);
    });

  inflight.set(inflightKey, promise);
  return promise;
}

export function getCachedReportPreviewFirstChunk(
  propertyMasterId,
  inspectionReferenceNumber,
) {
  const entry = chunkStore.get(
    cacheKey(propertyMasterId, inspectionReferenceNumber),
  );
  return entry?.data ?? null;
}

export function getCachedPreloadPageSize(
  propertyMasterId,
  inspectionReferenceNumber,
) {
  const entry = chunkStore.get(
    cacheKey(propertyMasterId, inspectionReferenceNumber),
  );
  return entry?.pageSize ?? null;
}

export function setCachedReportPreviewFirstChunk(
  propertyMasterId,
  inspectionReferenceNumber,
  data,
  pageSize = CHUNK_PAGE_SIZE,
) {
  if (!data) return;
  chunkStore.set(cacheKey(propertyMasterId, inspectionReferenceNumber), {
    data,
    pageSize,
    fetchedAt: Date.now(),
  });
}

export function clearReportPreviewCache(
  propertyMasterId,
  inspectionReferenceNumber,
) {
  chunkStore.delete(cacheKey(propertyMasterId, inspectionReferenceNumber));
}

/**
 * Schedule preload after main screen is idle (does not block Property View Details).
 */
export function scheduleReportPreviewPreload(
  propertyMasterId,
  inspectionReferenceNumber,
) {
  const run = () => {
    prefetchReportPreviewFirstChunk(
      propertyMasterId,
      inspectionReferenceNumber,
      PRELOAD_PAGE_SIZE,
    );
  };

  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    const id = window.requestIdleCallback(run, { timeout: 4000 });
    return () => window.cancelIdleCallback(id);
  }

  const timer = window.setTimeout(run, 800);
  return () => window.clearTimeout(timer);
}

const PAGINATED_REPORT_KEYS = [
  "combinedDoors",
  "complianceChecks",
  "physicalMeasurements",
  "additionalInfos",
  "inspectedDoors",
  "doorSummaryDto",
];

/**
 * Replace first-chunk door arrays with a full PageNumber=1 / PageSize=30 response.
 */
export function mergeFirstChunkUpgrade(previous, fullPageResponse) {
  if (!previous || !fullPageResponse) {
    return previous ?? fullPageResponse;
  }

  const nextPir = fullPageResponse.propertyInspectionReport ?? {};
  return {
    ...previous,
    ...fullPageResponse,
    propertyInspectionReport: {
      ...(previous.propertyInspectionReport ?? {}),
      ...(fullPageResponse.propertyInspectionReport ?? {}),
      ...PAGINATED_REPORT_KEYS.reduce((acc, key) => {
        if (nextPir[key] !== undefined) {
          acc[key] = nextPir[key];
        }
        return acc;
      }, {}),
    },
  };
}
